import type { Rules } from "async-validator"
import type { FormItemRule, FormRules } from "element-plus"
// 请查看 validator.js，了解可与 Refinements 结合使用的大量其他有用字符串验证函数。
import type { BaseTableColumns } from "@/components/table/types"
import Schema from 'async-validator'
import dayjs from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"
import { codeToText } from 'element-china-area-data'
import { isNil } from "lodash-es"

import { z } from "@/plugins/zod"

dayjs.extend(customParseFormat)

export const isExternal = (path: string) => {
  const reg = /^(https?:|mailto:|tel:)/
  return reg.test(path)
}

export const isArray = (arg: any) => {
  if (typeof Array.isArray === "undefined") {
    return Object.prototype.toString.call(arg) === "[object Array]"
  }
  return Array.isArray(arg)
}

export function isString(str: any) {
  if (typeof str === "string" || str instanceof String) {
    return true
  }
  return false
}

export const isValidURL = (url: string) => {
  const reg =
    /^(https?|ftp):\/\/([a-zA-Z0-9.-]+(:[a-zA-Z0-9.&%$-]+)*@)*((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9][0-9]?)(\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])){3}|([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+\.(com|edu|gov|int|mil|net|org|biz|arpa|info|name|pro|aero|coop|museum|[a-zA-Z]{2}))(:[0-9]+)*(\/($|[a-zA-Z0-9.,?'\\+&%$#=~_-]+))*$/
  return reg.test(url)
}

/**
 * 用于检查一个字符串是否为空或者只包含空白字符（例如空格、制表符、换行符等）
 * @param str 待校验的字符串
 */
export const isBlank = (str: string) => {
  const regex = /^\s*$/
  return regex.test(str)
}

export const validateSkuName = z
  .string()
  .max(8, { message: "商品名称不能超过 8 个字符" })
  .min(1, { message: "商品名称不能为空" })

// 验证函数
function validateField(row: any, property: string, rule: FormItemRule) {
  const value = row[property]
  if (rule.validator) {
    let err = null
    rule.validator(
      rule,
      value,
      (error) => {
        err = error
      },
      row,
      {},
    )
    return err
  }
}

/**
 * 验证数组对象数据是否符合规则，一般用于表格业务
 * @param rules
 * @param dataArray
 * @returns
 */
export function validateArray(rules: FormRules, dataArray: any[], columns: BaseTableColumns<any>) {
  let allValid = true
  const errors: any[] = []
  dataArray.forEach((row, index) => {
    if (!row.errorMessages) {
      row.errorMessages = {}
    }
    const itemErrors: any = {}
    Object.keys(rules).forEach((property) => {
      const cellIndex = columns.findIndex((column) => column.prop === property)
      const ruleArray = rules[property] as any[]
      ruleArray.forEach((rule) => {
        const error = validateField(row, property, rule)
        if (error) {
          if (!itemErrors[cellIndex]) {
            itemErrors[cellIndex] = []
          }
          itemErrors[cellIndex].push(error)
          allValid = false
        }
      })
    })
    // JZTAG side-effect hack
    row.errorMessages = itemErrors
    if (Object.keys(itemErrors).length > 0) {
      errors.push({ index, errors: itemErrors })
    }
  })

  return { valid: allValid, errors }
}

/**
 * 如果是null、undefined、''，则返回true，否则返回false
 */
const isNilOrEmptyStr = (value: any): value is null | undefined | '' => {
  return (isNil(value) || value === '')
}

const optionalCommon = [
  z.undefined(),
  z.null(),
  z.literal(''),
] as const

const optionalCommonValidator = z.union(optionalCommon)

/**
 * 验证非必填的数字，如果填写则必须为精度小于等于2的数字
 * 允许的格式：
 * - 空字符串 ''
 * - undefined
 * - null
 * - number: 1, 42, 1.2, 0.35
 * - 字符串数字: '1', '42', '1.2', '0.35'
 */
const genNumberValidator = ({
  precision = 2,
  max,
  min,
}: {
  precision?: number
  max?: number
  min?: number
} = {}) => z.union([
  ...optionalCommon,
  z.number()
    .refine(
      (val) => {
        // 检查精度
        const str = Math.abs(val).toString()
        const decimalPart = str.split('.')[1]
        const isPrecisionValid = !decimalPart || decimalPart.length <= precision

        // 检查最大值和最小值
        const isMaxValid = max === undefined || val <= max
        const isMinValid = min === undefined || val >= min

        return isPrecisionValid && isMaxValid && isMinValid
      },
      (val) => {
        if (max !== undefined && val > max) return { message: `数值不能大于 ${max}` }
        if (min !== undefined && val < min) return { message: `数值不能小于 ${min}` }
        return { message: `请输入最多 ${precision} 位小数的数值` }
      },
    ),
  z.string()
    .refine(
      (val) => {
        if (!val) return true
        const regex = new RegExp(`^-?\\d+(\\.\\d{0,${precision}})?$`)
        const isFormatValid = regex.test(val)
        if (!isFormatValid) return false

        const numVal = Number(val)
        const isMaxValid = max === undefined || numVal <= max
        const isMinValid = min === undefined || numVal >= min

        return isFormatValid && isMaxValid && isMinValid
      },
      (val) => {
        const numVal = Number(val)
        if (max !== undefined && numVal > max) return { message: `数值不能大于 ${max}` }
        if (min !== undefined && numVal < min) return { message: `数值不能小于 ${min}` }
        return { message: `请输入最多 ${precision} 位小数的数值` }
      },
    )
    .transform((val) => (val === '' ? undefined : val)),
])

/**
 * @description 校验出生日期
 * @param {string} birth 8位出生日期 形如20210601
 * @returns {boolean} 校验是否通过
 */
const verifyBirthDate = (birth: string) => {
  // 格式化传入的日期格式为 yyyy-mm-dd
  const serializedBirth = `${birth.slice(0, 4)}-${birth.slice(
    4,
    6,
  )}-${birth.slice(6)}`
  const birthDate = new Date(serializedBirth).getTime()

  // 如果日期格式无效，形如 2020-13-14、2021-02-29 等，则返回 false
  // 无效的日期格式，Date.prototype.getTime() 会返回 NaN
  if (Number.isNaN(birthDate)) return false
  // 如果传入的日期领先于当前日期，比如传入 20990102 则返回 false
  if (birthDate > new Date().getTime()) return false
  // 否则返回 true，检验通过
  return true
}

/**
 * @description 校验码字符校验函数
 * @param {string} cardNum 传入的身份证号
 * @returns {boolean} 校验是否通过
 */
const verifyCheckCode = (cardNum: string) => {
  // 本体码
  const masterCode = cardNum.slice(0, 17).split("")
  // 校验码
  const checkCode = cardNum.slice(17).toLocaleLowerCase()
  // 校验因子
  const weightingFactor = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2]
  // 校验码换算关系
  const pattern: Record<number, number | string> = {
    0: 1,
    1: 0,
    2: "x",
    3: 9,
    4: 8,
    5: 7,
    6: 6,
    7: 5,
    8: 4,
    9: 3,
    10: 2,
  }
  // 各位值与对应校验因子相乘，并求和
  const sum = masterCode.reduce(
    (pre, cur, ix) => pre + Number(cur) * weightingFactor[ix],
    0,
  )
  // 累加值对11取余
  const val = sum % 11
  // 能与换算关系对应上则返回true，否则返回false
  if (String(pattern[val]) !== checkCode) return false
  return true
}

/**
 * 身份证号码校验的 Zod schema
 */
const validateIdCard = z.string().refine(
  (cardNum) => {
    // 对参数进行格式化
    const serializedCardNum = cardNum.toString().trim()

    if (serializedCardNum.length === 15) {
      // JZTODO 15位身份证号码校验
      return true
    }

    // 长度校验
    if (serializedCardNum.length !== 18) return false

    // 简单的正则校验
    if (!/^[1-9]\d{5}(18|19|[2-9]\d)\d{9}[0-9X]$/i.test(serializedCardNum))
      return false

    // 地址码检验
    const areaCode = serializedCardNum.slice(0, 6)
    if (!codeToText[areaCode]) return false

    // 出生日期码校验
    const birthCode = serializedCardNum.slice(6, 14)
    if (!verifyBirthDate(birthCode)) return false

    // 校验码校验
    if (!verifyCheckCode(serializedCardNum)) return false

    return true
  },
  {
    message: "请输入有效的身份证号码",
  },
)

/**
 * 正整数验证
 */
const validatePositiveInteger = z.union([
  ...optionalCommon,
  z.string().transform((val, ctx) => {
    const trimmedVal = val.trim()
    if (trimmedVal.length !== val.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "不能输入空白字符",
      })
      return z.NEVER
    }
    const numVal = Number(trimmedVal)
    if (trimmedVal === '' || isNaN(numVal)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "请输入数值类型的数据",
      })
      return z.NEVER
    }
    return numVal
  }).refine(
    (val) => {
      const isInteger = Number.isInteger(val)
      return isInteger && val >= 0 && val <= Number.MAX_SAFE_INTEGER
    },
    (val) => {
      const isInteger = Number.isInteger(val)
      if (!isInteger) return { message: "请输入整数" }
      if (val > Number.MAX_SAFE_INTEGER) return { message: `请输入小于 ${Number.MAX_SAFE_INTEGER} 的数值` }
      return { message: "请输入大于等于0的数值" }
    },
  ),
  z.number()
    .refine(
      (val) => {
        const isInteger = Number.isInteger(val)
        return isInteger && val >= 0 && val <= Number.MAX_SAFE_INTEGER
      },
      (val) => {
        const isInteger = Number.isInteger(val)
        if (!isInteger) return { message: "请输入整数" }
        if (val > Number.MAX_SAFE_INTEGER) return { message: `请输入小于 ${Number.MAX_SAFE_INTEGER} 的数值` }
        return { message: "请输入大于等于0的数值" }
      },
    ),
  z.any().refine(() => {
    return false
  }, { message: "请输入数值类型的数据" }),
])

/**
 * 字符串，不能有首尾空格
 */
const genValidateString = ({
  name,
  min,
  max,
}: {
  name?: string
  min?: number
  max?: number
}) => z.string({
  errorMap: (issue, ctx) => {
    const code = issue.code
    switch (code) {
      case z.ZodIssueCode.too_small:
        return { message: issue.message ?? `${name}不能少于${min}个字符` }
      case z.ZodIssueCode.too_big:
        return { message: issue.message ?? `${name}不能多于${max}个字符` }
      default:
        return { message: ctx.defaultError }
    }
  },
}).trim().min(min ?? 1).max(max ?? 100).refine((val) => {
  const trimmedVal = val.trim()
  return trimmedVal.length === val.length
}, {
  message: `${name}首尾不能有空白字符`,
})

// const simplePositiveIntegerValidator = z.union([
//   z.undefined(),
//   z.null(),
//   z.literal(''),
//   // z.string().transform()
// ])

/**
 * 获取一个用于验证数组对象的 async-validator 实例
 * @param rules 对象校验规则
 * @param field 数组字段名，默认为 'items'
 * @returns async-validator 实例
 */
const getArrayValidator = (rules: Rules, field = 'items') => {
  const descriptor: Rules = { [field]: [{ type: 'array', required: true, defaultField: { type: "object", required: true, fields: rules } }] }
  const validator = new Schema(descriptor)
  return validator
}

/**
 * 转换生日为字符串格式
 * @param birthdayValue 生日值
 * @returns 格式化的日期字符串，无效日期抛出异常
 */
export const convertBirthdayToString = (birthdayValue: any): string | null => {
  if (!birthdayValue || birthdayValue === '' || birthdayValue === null || birthdayValue === undefined) {
    return null
  }

  const birthdayStr = String(birthdayValue).trim()
  let dayjsDate: dayjs.Dayjs

  if (typeof birthdayValue === 'number') {
    const excelDate = new Date((birthdayValue - 25569) * 86400 * 1000)
    dayjsDate = dayjs(excelDate)
  } else {
    dayjsDate = dayjs(birthdayStr, ['YYYY-MM-DD', 'YYYY/MM/DD', 'YYYY-M-D', 'YYYY/M/D'], true)
  }

  if (!dayjsDate.isValid()) {
    throw new Error(`日期"${birthdayStr}"格式不合法，请检查后重新上传`)
  }

  if (dayjsDate.isAfter(dayjs())) {
    throw new Error(`日期"${birthdayStr}"不能是未来日期`)
  }

  return dayjsDate.format('YYYY-MM-DD')
}

export const validateOptionalPrecision2Number = genNumberValidator({ precision: 2 })
export { genNumberValidator, genValidateString, getArrayValidator, isNilOrEmptyStr, optionalCommonValidator, validateIdCard, validatePositiveInteger }
